import { MarketType } from '../types/marketTypes';

/** Catalog for the Market Controls screen — the single place to add a sport or market type (no backend catalog endpoint). */

export interface SportOption {
  id: number | null;
  label: string;
}

// Internal sport ids (not GR8 external ids); null = all sports.
export const SPORT_OPTIONS: SportOption[] = [
  { id: null, label: 'All sports' },
  { id: 1, label: 'Cricket' },
  { id: 2, label: 'Table Tennis' },
  { id: 3, label: 'Tennis' },
];

// Human-readable labels for market-type enum names.
export const MARKET_TYPE_LABELS: Record<MarketType, string> = {
  [MarketType.TWO_WAY_MATCH_ODDS]: 'Match Odds (2-way)',
  [MarketType.THREE_WAY_MATCH_ODDS]: 'Match Odds (3-way)',
  [MarketType.INNING_RUNS]: 'Inning Runs',
  [MarketType.NEXT_BALL]: 'Next Ball',
  [MarketType.TOSS]: 'Toss',
  [MarketType.TEAM_4S]: 'Team 4s',
  [MarketType.TEAM_6S]: 'Team 6s',
  [MarketType.TOTAL_4S]: 'Total 4s',
  [MarketType.TOTAL_6S]: 'Total 6s',
  [MarketType.MATCH_TOTAL_RUNS]: 'Match Total Runs',
  [MarketType.MATCH_TOTAL_WICKETS]: 'Match Total Wickets',
  [MarketType.TEAM_ODD_EVEN]: 'Team Odd/Even',
  [MarketType.MATCH_ODD_EVEN]: 'Match Odd/Even',
  [MarketType.TT_MATCH_WINNER]: 'Match Winner',
  [MarketType.TT_MATCH_TOTAL_ODD_EVEN]: 'Match Total Points Odd/Even',
  [MarketType.TT_SET_WINNER]: 'Set Winner',
  [MarketType.TT_MATCH_SETS_HANDICAP]: 'Match Sets Handicap',
  [MarketType.TT_RACE_TO_N]: 'Race to N Points',
  [MarketType.TT_SET_POINTS_HANDICAP]: 'Set Points Handicap',
  [MarketType.TT_SET_TOTAL_POINTS]: 'Set Total Points',
  [MarketType.TT_MATCH_TOTAL_POINTS]: 'Match Total Points',
  [MarketType.TT_SET_TOTAL_ODD_EVEN]: 'Set Total Points Odd/Even',
  [MarketType.TT_MATCH_CORRECT_SCORE]: 'Match Correct Score',
  [MarketType.TT_SET_POINT_N_WINNER]: 'Point N Winner',
  [MarketType.TENNIS_MATCH_WINNER]: 'Match Winner',
  [MarketType.TENNIS_MATCH_TOTAL_ODD_EVEN]: 'Match Total Games Odd/Even',
  [MarketType.TENNIS_MATCH_TOTAL_GAMES]: 'Match Total Games',
  [MarketType.TENNIS_MATCH_GAMES_HANDICAP]: 'Match Games Handicap',
  [MarketType.TENNIS_MATCH_RACE_TO_N]: 'Match Race to N Games',
  [MarketType.TENNIS_SET_GAME_WINNER]: 'Set Game Winner',
  // Never offered as a control: no rule can name a type this frontend cannot identify.
  [MarketType.UNKNOWN]: 'Unmapped',
};

const CRICKET_MARKET_TYPES: MarketType[] = [
  MarketType.TWO_WAY_MATCH_ODDS,
  MarketType.THREE_WAY_MATCH_ODDS,
  MarketType.INNING_RUNS,
  MarketType.NEXT_BALL,
  MarketType.TOSS,
  MarketType.TEAM_4S,
  MarketType.TEAM_6S,
  MarketType.TOTAL_4S,
  MarketType.TOTAL_6S,
  MarketType.MATCH_TOTAL_RUNS,
  MarketType.MATCH_TOTAL_WICKETS,
  MarketType.TEAM_ODD_EVEN,
  MarketType.MATCH_ODD_EVEN,
];

const TABLE_TENNIS_MARKET_TYPES: MarketType[] = [
  MarketType.TT_MATCH_WINNER,
  MarketType.TT_MATCH_TOTAL_ODD_EVEN,
  MarketType.TT_SET_WINNER,
  MarketType.TT_MATCH_SETS_HANDICAP,
  MarketType.TT_RACE_TO_N,
  MarketType.TT_SET_POINTS_HANDICAP,
  MarketType.TT_SET_TOTAL_POINTS,
  MarketType.TT_MATCH_TOTAL_POINTS,
  MarketType.TT_SET_TOTAL_ODD_EVEN,
  MarketType.TT_MATCH_CORRECT_SCORE,
  MarketType.TT_SET_POINT_N_WINNER,
];

const TENNIS_MARKET_TYPES: MarketType[] = [
  MarketType.TENNIS_MATCH_WINNER,
  MarketType.TENNIS_MATCH_RACE_TO_N,
  MarketType.TENNIS_MATCH_GAMES_HANDICAP,
  MarketType.TENNIS_MATCH_TOTAL_GAMES,
  MarketType.TENNIS_MATCH_TOTAL_ODD_EVEN,
  MarketType.TENNIS_SET_GAME_WINNER,
];

const MARKET_TYPES_BY_SPORT: Record<number, MarketType[]> = {
  1: CRICKET_MARKET_TYPES,
  2: TABLE_TENNIS_MARKET_TYPES,
  3: TENNIS_MARKET_TYPES,
};

/** Market types for the selected sport; the union of all sports when no sport is chosen. */
export function marketTypesForSport(sportId: number | null): MarketType[] {
  if (sportId != null) {
    return MARKET_TYPES_BY_SPORT[sportId] ?? [];
  }
  const seen = new Set<MarketType>();
  Object.values(MARKET_TYPES_BY_SPORT).forEach(types => types.forEach(t => seen.add(t)));
  return Array.from(seen);
}

export function sportLabel(sportId: number | null): string {
  if (sportId == null) return 'All sports';
  return SPORT_OPTIONS.find(o => o.id === sportId)?.label ?? `Sport ${sportId}`;
}

/** Label for a market-type enum name; falls back to the raw name. */
export function marketTypeLabel(marketType: string | null): string {
  if (!marketType) return 'All market types';
  return MARKET_TYPE_LABELS[marketType as MarketType] ?? marketType;
}
